import { useState, useEffect, useMemo } from 'react'
import Image from 'next/image'
import { BlockCall } from 'types/block-call'
import { HiChevronDoubleRight } from 'react-icons/hi'
type Props = {
  groupCompanies: BlockCall[]
}

export const OurGroupCarousel = ({ groupCompanies }: Props) => {
  const [startIndex, setStartIndex] = useState(0)
  const [isAutoPlaying, setIsAutoPlaying] = useState(true)
  const [itemsPerPage, setItemsPerPage] = useState(4)

  // determine itemsPerPage responsively (SSR-safe)
  useEffect(() => {
    const update = () => {
      if (typeof window === 'undefined') return
      const w = window.innerWidth
      if (w >= 1024) setItemsPerPage(4)
      else if (w >= 400) setItemsPerPage(2)
      else setItemsPerPage(1)
    }

    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  // Get current visible items with continuous wrapping
  const visibleItems = useMemo(() => {
    if (!groupCompanies || groupCompanies.length === 0) return []
    const items: BlockCall[] = []
    for (let i = 0; i < itemsPerPage; i++) {
      const index = (startIndex + i) % groupCompanies.length
      items.push(groupCompanies[index])
    }
    return items
  }, [groupCompanies, startIndex, itemsPerPage])

  // Calculate total possible positions for dots
  const totalPositions = useMemo(() => {
    if (!groupCompanies || groupCompanies.length === 0) return 0
    return groupCompanies.length
  }, [groupCompanies])

  // Autoplay a cada 10 segundos
  useEffect(() => {
    if (
      !isAutoPlaying ||
      !groupCompanies ||
      groupCompanies.length <= itemsPerPage
    )
      return
    const interval = setInterval(() => {
      setStartIndex(prev => (prev + 1) % groupCompanies.length)
    }, 10000) // 10 segundos
    return () => clearInterval(interval)
  }, [isAutoPlaying, groupCompanies, itemsPerPage])

  const nextItems = () => setStartIndex(p => (p + 1) % groupCompanies.length)
  const prevItems = () =>
    setStartIndex(p => (p - 1 + groupCompanies.length) % groupCompanies.length)
  const goToPosition = (i: number) => setStartIndex(i)
  const handleMouseEnter = () => setIsAutoPlaying(false)
  const handleMouseLeave = () => setIsAutoPlaying(true)

  if (!groupCompanies || groupCompanies.length === 0) return null

  return (
    <section className="container mx-auto px-4 py-8">
      <h2 className="text-3xl font-bold text-center mb-12">Nosso Grupo</h2>
      {/* <p className="text-xs text-v2-eerie-black text-center md:text-base py-2">
        Conheça nossas ferramentas de operação{' '}
        <br />
        e manutenção de usinas solares
      </p> */}
      <div
        className="relative w-full"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="relative overflow-hidden rounded-lg">
          <div className="transition-all duration-500 ease-in-out">
            <div
              className={`grid gap-4 ${
                itemsPerPage === 1
                  ? 'grid-cols-1'
                  : itemsPerPage === 2
                  ? 'grid-cols-2'
                  : 'grid-cols-4'
              }`}
            >
              {visibleItems.map((company, idx) => (
                <div
                  key={`${startIndex}-${idx}`}
                  className="rounded-lg shadow-sm overflow-hidden h-80 flex flex-col items-center justify-center relative"
                >
                  <div className="w-full h-full flex">
                    <div className="relative w-full h-full ">
                      <Image
                        src={
                          (company as any).imageUrl ||
                          (company as any).logo ||
                          '/assets/default-logo.png'
                        }
                        alt={
                          (company as any).imageAlt ||
                          (company as any).name ||
                          'Logo da empresa'
                        }
                        fill
                        className="object-cover"
                        loading="lazy"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col  items-center justify-end absolute w-full rounded-lg bottom-0 h-full right-0 bg-gradient-to-t from-black/60 to-transparent  to-30% p-4 text-white">
                    {company.buttonLink !== '' && (
                      <a href={company.buttonLink || '#'}>
                        <button className="mt-2 px-4 py-2 text-sm  text-white rounded-lg flex  items-center">
                          {company.buttonText || 'Saiba Mais'}
                          <HiChevronDoubleRight
                            size={12}
                            className={
                              'w-full max-w-[12px] fill-[#ffffff] ml-1 mb-[1px]'
                            }
                          />
                        </button>
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Arrows - sempre visíveis se mais de 1 item */}
        {groupCompanies && groupCompanies.length > 1 && (
          <>
            <button
              onClick={prevItems}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors duration-200 z-10"
              aria-label="Itens anteriores"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <button
              onClick={nextItems}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors duration-200 z-10"
              aria-label="Próximos itens"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </>
        )}

        {/* Dots - sempre visíveis se mais de 1 item */}
        {groupCompanies && groupCompanies.length > 1 && (
          <div className="flex justify-center mt-6 space-x-2">
            {Array.from({ length: groupCompanies.length }, (_, i) => (
              <button
                key={i}
                onClick={() => goToPosition(i)}
                className={`w-3 h-3 rounded-full transition-colors duration-200  ${
                  i === startIndex
                    ? 'w-7 bg-gray-400'
                    : 'hover:bg-gray-400 bg-gray-200 hover:w-7'
                }`}
                aria-label={`Ir para posição ${i + 1}`}
              />
            ))}
          </div>
        )}

        {/* Counter */}
        {/* {groupCompanies && groupCompanies.length > itemsPerPage && (
          <div className="text-center mt-4 text-sm text-gray-600">
            Mostrando {itemsPerPage} de {groupCompanies.length} itens
          </div>
        )} */}
      </div>
    </section>
  )
}
